/**
 * Device-capability snapshot for local ASR performance triage.
 *
 * Local ASR is CPU-bound on the device, so "why was this student's decode slower than
 * real time" is unanswerable without knowing the hardware — and nothing in the session
 * logs identified it. The user agent cannot: Chrome's User-Agent Reduction replaces the
 * platform token with a frozen literal (every ChromeOS device reports
 * `X11; CrOS x86_64 14541.0.0` regardless of the actual board), so a field report from a
 * slow Chromebook was indistinguishable from one on new hardware. The real values live
 * behind User-Agent Client Hints and the `navigator` capability properties gathered here.
 *
 * Threading matters as much as the CPU: the Vosk runtime is a single-threaded WASM build,
 * and `crossOriginIsolated`/`sharedArrayBuffer` additionally gate whether any threaded
 * runtime could use more than one core, so both are recorded even though the current
 * build cannot benefit from them.
 */

import { runtimeChecks } from './engine/runtime_checks';
import { recordLocalAsrLifecycle } from './localAsrLifecycle';

/** High-entropy UA-CH hints worth having; unavailable ones are simply absent. */
const UA_HINTS = ['platformVersion', 'model', 'architecture', 'bitness'];

/**
 * Synchronous capability snapshot. Every field is a constant for the browser session.
 * @returns {object}
 */
export function getLocalAsrDeviceProfile() {
  const { simd, sharedArrayBuffer, crossOriginIsolated, hardwareConcurrency } = runtimeChecks();
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  return {
    simd,
    sharedArrayBuffer,
    crossOriginIsolated,
    // Logical cores. The Vosk WASM build is single-threaded, so this is a ceiling on what
    // a threaded build could recover, not a description of current behaviour.
    hardwareConcurrency,
    // Approximate RAM in GiB, Chromium-only and coarsely bucketed. Absent elsewhere.
    deviceMemory: nav.deviceMemory,
    platform: nav.userAgentData?.platform,
  };
}

/**
 * Resolves the high-entropy UA-CH hints, or an empty object when unavailable.
 *
 * Never rejects: the API is absent outside Chromium and can reject for hints a permissions
 * policy disallows, neither of which should cost the caller its whole profile event.
 * @returns {Promise<object>}
 */
async function getUserAgentHints() {
  const uaData = typeof navigator !== 'undefined' ? navigator.userAgentData : null;
  if (!uaData || typeof uaData.getHighEntropyValues !== 'function') {
    return {};
  }
  try {
    const { platformVersion, model, architecture, bitness } = await uaData.getHighEntropyValues(UA_HINTS);
    // `model` is empty on desktop/ChromeOS and only populated on Android, so it is
    // omitted rather than recorded as an empty string.
    return { platformVersion, model: model || undefined, architecture, bitness };
  } catch {
    return {};
  }
}

/**
 * Records the device profile to the local ASR lifecycle timeline.
 *
 * Awaits the UA-CH hints so one event carries the whole picture, and records the
 * synchronous fields regardless of whether those hints resolve — a profile missing the
 * platform version is still worth far more than none.
 * @returns {Promise<void>}
 */
export async function recordLocalAsrDeviceProfile() {
  const profile = getLocalAsrDeviceProfile();
  const hints = await getUserAgentHints();
  recordLocalAsrLifecycle('enablement.deviceProfile', { ...profile, ...hints });
}
